/**
 * DriverTripsPage — DSL-driven trip list for drivers.
 *
 * All business rules (status groups, colors, trip types, card layout)
 * come from DSL config objects in `src/dsl/driver/`.
 * This page is a thin data-fetching + rendering wrapper.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Truck, Package, MapPin, Clock, RefreshCw,
  ChevronRight, AlertTriangle, CheckCircle2, Circle,
} from 'lucide-react';
import {
  TRIP_STATUS_COLORS,
  TRIP_TYPE_LABELS,
  TRIP_CARD_DSL,
  groupTrip,
  ACTIVE_STATUSES,
  type TripGroup,
} from '@/dsl/driver/driver-config';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TripStop {
  id: string;
  trip_id: string;
  stop_seq: number;
  stop_type: string;
  stop_status: string;
  location_name: string | null;
  address: string | null;
  scheduled_arrival: string | null;
}

interface DriverTrip {
  tripId: string;
  tripNo: string;
  tripType: string;
  tripRole: string;
  tripStatus: string;
  dispatchStatus: string;
  scheduledDate: string;
  vehicleNo: string | null;
  jobId: string;
  jobNo: string;
  jobType: string;
  customerName: string | null;
  stops: TripStop[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function DriverTripsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [trips, setTrips] = useState<DriverTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<TripGroup | 'all'>('all');

  const loadTrips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<DriverTrip[]>('/tms/mobile/my-trips');
      setTrips(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load trips');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadTrips(); }, [loadTrips]);

  // Group using DSL config function
  const grouped = {
    active: trips.filter(t => groupTrip(t.tripStatus) === 'active'),
    upcoming: trips.filter(t => groupTrip(t.tripStatus) === 'upcoming'),
    completed: trips.filter(t => groupTrip(t.tripStatus) === 'completed'),
  };

  const displayed = filter === 'all' ? trips : grouped[filter];

  // Filter pill definitions — driven by group config
  const filterPills: Array<{ key: TripGroup | 'all'; label: string; count: number }> = [
    { key: 'all',       label: t('driver.all', 'All'),       count: trips.length },
    { key: 'active',    label: t('driver.active', 'Active'), count: grouped.active.length },
    { key: 'upcoming',  label: t('driver.upcoming', 'Upcoming'), count: grouped.upcoming.length },
    { key: 'completed', label: t('driver.done', 'Done'),     count: grouped.completed.length },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 pt-4 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {t('driver.myTrips', 'My Trips')}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t('driver.today', 'Today')} · {trips.length} {t('driver.trips', 'trips')}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={loadTrips} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* ── Status filter pills (DSL-driven) ── */}
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          {filterPills.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filter === key
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      {/* ── Trip cards (rendered using DSL card config) ── */}
      <div className="flex-1 overflow-auto px-4 py-3 space-y-2">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-20 w-full" /></CardContent></Card>
          ))
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm">
            <AlertTriangle className="h-8 w-8 text-destructive opacity-50 mb-2" />
            <p className="text-muted-foreground">{error}</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={loadTrips}>Retry</Button>
          </div>
        ) : displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm text-muted-foreground">
            <CheckCircle2 className="h-8 w-8 opacity-30 mb-2" />
            <p>{t('driver.noTrips', 'No trips found')}</p>
          </div>
        ) : (
          <>
            {filter === 'all' && grouped.active.length > 0 && (
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide px-1">
                {t('driver.inProgress', 'In Progress')}
              </p>
            )}
            {(filter === 'all' ? [...grouped.active, ...grouped.upcoming, ...grouped.completed] : displayed).map((trip, idx) => {
              const showUpcomingLabel = filter === 'all' && idx === grouped.active.length && grouped.upcoming.length > 0;
              const showCompletedLabel = filter === 'all' && idx === grouped.active.length + grouped.upcoming.length && grouped.completed.length > 0;

              return (
                <div key={trip.tripId}>
                  {showUpcomingLabel && (
                    <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide px-1 mt-3 mb-1">
                      {t('driver.upcoming', 'Upcoming')}
                    </p>
                  )}
                  {showCompletedLabel && (
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide px-1 mt-3 mb-1">
                      {t('driver.completed', 'Completed')}
                    </p>
                  )}
                  <DslTripCard
                    trip={trip}
                    onClick={() => navigate(`/driver/trip/${trip.tripId}`)}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// ─── DSL-driven TripCard ─────────────────────────────────────────────────────

/**
 * Renders a trip card using DSL config from TRIP_CARD_DSL.
 * Card layout fields (title, badge, body fields) come from config.
 */
function DslTripCard({ trip, onClick }: { trip: DriverTrip; onClick: () => void }) {
  const dsl = TRIP_CARD_DSL;
  const firstStop = trip.stops[0];
  const lastStop = trip.stops[trip.stops.length - 1];

  // Read fields from DSL config
  const title = trip[dsl.titleField as keyof DriverTrip] as string;
  const badge = dsl.badgeField ? (trip[dsl.badgeField as keyof DriverTrip] as string) : undefined;
  const subtitle = dsl.subtitleField ? (trip[dsl.subtitleField as keyof DriverTrip] as string) : undefined;

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${
        ACTIVE_STATUSES.has(trip.tripStatus) ? 'ring-2 ring-amber-300' : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="p-3 space-y-2">
        {/* Row 1: Title + Status badge + Type label (from DSL) */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{title || trip.tripId.slice(0, 8)}</span>
            {subtitle && (
              <Badge variant="outline" className="text-[10px] px-1.5">
                {TRIP_TYPE_LABELS[subtitle] ?? subtitle}
              </Badge>
            )}
          </div>
          {badge && (
            <Badge className={`text-[10px] ${TRIP_STATUS_COLORS[badge] ?? 'bg-gray-100 text-gray-600'}`}>
              {badge.replace(/_/g, ' ')}
            </Badge>
          )}
        </div>

        {/* Row 2: Body fields from DSL */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Package className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">
            {(dsl.bodyFields ?? [])
              .map(f => trip[f as keyof DriverTrip] as string)
              .filter(Boolean)
              .join(' · ') || '—'}
          </span>
        </div>

        {/* Row 3: From → To (stop-based, not DSL field) */}
        {(firstStop || lastStop) && (
          <div className="flex items-center gap-1.5 text-xs">
            <MapPin className="h-3.5 w-3.5 text-green-600 shrink-0" />
            <span className="truncate">{firstStop?.location_name ?? '—'}</span>
            <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            <MapPin className="h-3.5 w-3.5 text-red-500 shrink-0" />
            <span className="truncate">{lastStop?.location_name ?? firstStop?.location_name ?? '—'}</span>
          </div>
        )}

        {/* Row 4: Vehicle + Stops count + Time */}
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <div className="flex items-center gap-3">
            {trip.vehicleNo && (
              <span className="flex items-center gap-1">
                <Truck className="h-3 w-3" /> {trip.vehicleNo}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Circle className="h-3 w-3" /> {trip.stops.length} stops
            </span>
          </div>
          {trip.scheduledDate && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(trip.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
